/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { EntryPoint, LogCategory } from '../../../logger';
import { useCloseKeydown, useNotifySuccessInCreatRequest } from '../../../utilities/CustomHookUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ScrollBoundary } from '../../common';
import { ChooseTemplate } from '../ChooseTemplate';
import { getTitleStyles } from './CreateRequestV2.styles';

export interface CreateRequestV2Props {
    // Should show back button nav bar (applies for mobile personal app)
    showNavigationBar?: boolean;
    // Callback on dismissing the modal of this component when specified
    onDismiss?: () => void;
    // The custom footer component which will be rendered in the modal footer
    customFooterComponent?: JSX.Element;
}

export const CreateRequestV2: React.FC<CreateRequestV2Props> = (props: CreateRequestV2Props): JSX.Element => {
    const { onDismiss, showNavigationBar, customFooterComponent } = props;

    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();

    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const modalHeader = (
        <Label role='alert' aria-label={loc.strings.create_approval_title} styles={getTitleStyles(theme)}>
            {loc.strings.create_approval_title}
        </Label>
    );

    const entryPoint = onDismiss || showNavigationBar ? EntryPoint.PersonalApp : EntryPoint.ComposeExtension;

    React.useEffect((): void => {
        logger.logTraceInfo(
            LogCategory.CreateApprovalView,
            `Loaded create approvalV2 inner form. Entry Point: ${entryPoint}`
        );
    }, [logger, entryPoint]);

    useCloseKeydown(entryPoint);

    useNotifySuccessInCreatRequest(entryPoint, true);

    const modalContainer = (
        <div className='topContainer modal-container desktop with-footer'>
            {modalHeader}
            <div className='approval-scrollable-content'>
                <ScrollBoundary isTopBoundary />
                <Stack>
                    <ChooseTemplate entryPoint={entryPoint} tiled />
                </Stack>
                <ScrollBoundary isTopBoundary={false} />
            </div>
            <div className='modal-footer'>
                <Stack horizontal horizontalAlign='start'>
                    <div className='button-group'>{customFooterComponent}</div>
                </Stack>
            </div>
        </div>
    );

    return modalContainer;
};
